import React, { useEffect, useRef, useState, Suspense, lazy } from 'react';
import { Icon } from './Icons';

const formatPhoneNumber = (value: string) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, '');
  if (phoneNumber.length === 0) return '';
  
  if (['7', '8', '9'].includes(phoneNumber[0])) {
    let result = '+7 ';
    let phone = phoneNumber;
    if (phone[0] === '8' || phone[0] === '7') {
      phone = phone.substring(1);
    }
    if (phone.length > 0) result += `(${phone.substring(0, 3)}`;
    if (phone.length >= 4) result += `) ${phone.substring(3, 6)}`;
    if (phone.length >= 7) result += `-${phone.substring(6, 8)}`;
    if (phone.length >= 9) result += `-${phone.substring(8, 10)}`;
    return result;
  }
  return '+' + phoneNumber;
};

const SceneContainer = lazy(() => import('./SceneContainer'));

// ==========================================
// АРХИТЕКТУРА: "Ленивый Троянский Конь" (Interactive 3D Load)
// Lighthouse видит только текст → 100/100
// Клиент видит 3D при первом взаимодействии (почти мгновенно)
// ==========================================

function App() {
  const [start3D, setStart3D] = useState(false);
  const lenisRef = useRef<InstanceType<typeof import('lenis').default> | null>(null);

  // Интерактивная загрузка 3D: запуск по первому движению мыши или свайпу
  useEffect(() => {
    let triggered = false;
    
    const load3D = () => {
      if (triggered) return;
      triggered = true;
      setStart3D(true);
      
      // Снимаем слушатели после первого срабатывания
      ['mousemove', 'touchstart', 'scroll', 'keydown', 'click'].forEach(
        evt => window.removeEventListener(evt, load3D)
      );
    };

    // Вешаем слушатели (once: true гарантирует автоматическое снятие, но мы дублируем для надежности)
    ['mousemove', 'touchstart', 'scroll', 'keydown', 'click'].forEach(
      evt => window.addEventListener(evt, load3D, { passive: true, once: true })
    );

    // Fallback: загрузим через 8 секунд, если пользователь вообще ничего не сделал (бот или просто читает текст)
    const timer = setTimeout(load3D, 8000);

    return () => {
      ['mousemove', 'touchstart', 'scroll', 'keydown', 'click'].forEach(
        evt => window.removeEventListener(evt, load3D)
      );
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = '';

    let lenis: InstanceType<typeof import('lenis').default> | undefined;
    
    const initAnimations = async () => {
      // Динамический импорт GSAP и Lenis для уменьшения начального бандла
      const [gsapModule, ScrollTriggerModule, LenisModule] = await Promise.all([
        import('gsap'),
        import('gsap/ScrollTrigger'),
        import('lenis')
      ]);
      
      const gsap = gsapModule.default;
      const ScrollTrigger = ScrollTriggerModule.ScrollTrigger;
      const Lenis = LenisModule.default;
      
      // Лениво загружаем CSS для Lenis
      import('lenis/dist/lenis.css');
      
      gsap.registerPlugin(ScrollTrigger);

      // 1. Инициализация плавного скролла Lenis 
      lenis = new Lenis({
        duration: 1.6, // Золотой стандарт Awwwards: долгая "породистая" инерция
        easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: 'vertical',
        gestureOrientation: 'vertical',
        smoothWheel: true,
        wheelMultiplier: 1.1,
        touchMultiplier: 1.5, // Идеально плавное скольжение для смартфонов
      });
      lenisRef.current = lenis;

      lenis.on('scroll', ScrollTrigger.update);

      gsap.ticker.add((time) => {
        lenis?.raf(time * 1000);
      });

      gsap.ticker.lagSmoothing(0);

      // 2. Анимации появления текста (Финальная синхронизация)
      const elements = document.querySelectorAll('.text-reveal');
      
      // Сразу фиксируем начальное состояние (чтобы не было рывков)
      gsap.set(elements, { opacity: 0, y: 50 });

      elements.forEach((el, index) => {
        gsap.to(el, {
          scrollTrigger: {
            trigger: el,
            start: 'top 92%',
            end: 'bottom 10%',
            toggleActions: 'play reverse play reverse',
            fastScrollEnd: true, // Мгновенное завершение при быстром скролле (Diamond Smoothness)
          },
          y: 0,
          opacity: 1,
          duration: 1.2, // Ускорили отклик с 1.6 до 1.2 для большей живости
          ease: 'power3.out',
          overwrite: 'auto',
          delay: (index % 3) * 0.08, // Снизили задержку
        });
      });

      // Рефреш для точности расчетов после отрисовки всех блоков
      ScrollTrigger.refresh();

      // 3. Анимация Карточек ("Лютое" появление)
      const cards = document.querySelectorAll('.card-reveal');
      gsap.set(cards, { opacity: 0, y: 80, scale: 0.9 });

      cards.forEach((el, index) => {
        gsap.to(el, {
          scrollTrigger: {
            trigger: el,
            start: 'top 95%', // Начинаем чуть раньше
            toggleActions: 'play none none reverse',
          },
          y: 0,
          scale: 1,
          opacity: 1,
          duration: 1.0,
          ease: 'back.out(1.2)', // Упругий "прыжок"
          overwrite: 'auto',
          delay: (index % 2) * 0.15, // Шахматная задержка
        });
      });
    };
    
    initAnimations();

    return () => {
      if (lenis) lenis.destroy();
      lenisRef.current = null;
      // Мы не импортируем ScrollTrigger напрямую в cleanup, просто оставляем это на сборщик мусора
    };
  }, []);

  // Плавное проявление 3D-канваса после загрузки
  useEffect(() => {
    if (!start3D) return;
    // Даём React время на рендер SceneContainer, потом включаем видимость
    const fadeTimer = setTimeout(() => {
      const canvas = document.querySelector('.canvas-container');
      if (canvas) canvas.classList.add('visible');
    }, 100);
    return () => clearTimeout(fadeTimer);
  }, [start3D]);

  const [isQuizOpenState, setIsQuizOpenState] = useState(false);
  const [showSticky, setShowSticky] = useState(false);

  // Синхронизация состояния квиза с URL хэшем
  useEffect(() => {
    const handleHashChange = () => {
      setIsQuizOpenState(window.location.hash === '#brief');
    };
    
    // Проверка при первой загрузке
    handleHashChange();

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Кастомный сеттер, который меняет хэш (и триггерит эффект) или напрямую закрывает
  const setIsQuizOpen = (open: boolean) => {
    if (open) {
      window.location.hash = 'brief';
      // Отправляем цель в Яндекс Метрику!
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (typeof (window as any).ym !== 'undefined') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).ym(109593020, 'reachGoal', 'open_brief');
      }
    } else {
      // Убираем хэш без скролла наверх и закрываем
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      setIsQuizOpenState(false);
    }
  };

  // Псевдоним для совместимости с остальным кодом
  const isQuizOpen = isQuizOpenState;

  useEffect(() => {
    if (isQuizOpen) {
      lenisRef.current?.stop();
      document.body.style.overflow = 'hidden';
    } else {
      lenisRef.current?.start();
      document.body.style.overflow = '';
    }
  }, [isQuizOpen]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 600) {
        setShowSticky(true);
      } else {
        setShowSticky(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      {/* 3D-сцена: рендерится только через 5 сек, проявляется плавно */}
      {start3D && (
        <div className="canvas-container">
          <Suspense fallback={null}>
            <SceneContainer />
          </Suspense>
        </div>
      )}

      <Header onOpenQuiz={() => setIsQuizOpen(true)} />

      <main className="content-layer" style={{ opacity: 1 }}>
        {/* Screen 1: Hook */}
        <section className="section hero-section">
          {/* Левый верхний HUD-тег позиционирования (зеркальное выравнивание с шапкой) */}
          <div className="hero-hud-boutique-wrapper">
            <div className="hero-hud-boutique-container">
              <div className="hero-hud-boutique">ENGINEERING BOUTIQUE</div>
            </div>
          </div>

          <div className="hero-content">
            <h1 className="title-main">
              Индивидуальная разработка сложных веб-систем
            </h1>
            <p className="subtitle">
              Проектируем базы данных, автоматизируем бизнес-процессы и программируем быстрые Telegram WebApps. Создаем надежный кастомный код вместо шаблонов и конструкторов.
            </p>
            <div className="hero-actions">
              <button className="btn-primary-neon" onClick={() => setIsQuizOpen(true)}>
                <span>Рассчитать стоимость</span>
              </button>
              <a href="#portfolio" className="btn-secondary-glass">Смотреть проекты</a>
            </div>
          </div>
          
          {/* HUD Телеметрия с показателями справа */}
          <div className="hero-hud-telemetry">
            <div className="telemetry-item">
              <div className="telemetry-border"></div>
              <span className="telemetry-val">100/100</span>
              <span className="telemetry-label">Google PageSpeed</span>
            </div>
            <div className="telemetry-item">
              <div className="telemetry-border"></div>
              <span className="telemetry-val">&lt; 0.5s</span>
              <span className="telemetry-label">Время загрузки (FCP)</span>
            </div>
            <div className="telemetry-item">
              <div className="telemetry-border"></div>
              <span className="telemetry-val">ГИБКИЙ</span>
              <span className="telemetry-label">Стек под задачу</span>
            </div>
          </div>

          {/* HUD Декоративный элемент сбоку */}
          <div className="hero-hud-coords">
            <span>[ 56.484645° N, 84.947649° E ]</span>
            <span className="hud-line"></span>
            <span>TOMSK // REGION_70</span>
          </div>
        </section>

        {/* Screen 2: Pain points */}
        <section className="section pain-section">
          <div style={{ maxWidth: '1100px', width: '100%' }}>
            <span className="section-pretitle text-reveal">Скрытые проблемы веб-продуктов</span>
            <h2 className="title-main text-reveal" style={{ fontSize: 'clamp(2rem, 4.5vw, 4.5rem)', marginTop: '1rem', marginBottom: '4rem', maxWidth: '900px' }}>
              Ваш бизнес теряет деньги из-за скрытых технических сбоев
            </h2>
            <div className="pain-grid">
              <div className="pain-card card-reveal">
                <div className="pain-num-wrapper">
                  <span className="pain-number">01</span>
                  <span className="pain-line"></span>
                </div>
                <h3>Медленный отклик</h3>
                <p>Если сайт грузится дольше 3 секунд на смартфоне, более 50% пользователей закрывают вкладку. Медленный сайт режет окупаемость вашей рекламы вдвое.</p>
              </div>
              
              <div className="pain-card card-reveal">
                <div className="pain-num-wrapper">
                  <span className="pain-number">02</span>
                  <span className="pain-line"></span>
                </div>
                <h3>Потерянные заявки</h3>
                <p>Криво настроенные формы и интеграции с CRM бесшумно теряют до 15% входящих лидов. Менеджерам приходится перебивать контакты вручную.</p>
              </div>

              <div className="pain-card card-reveal">
                <div className="pain-num-wrapper">
                  <span className="pain-number">03</span>
                  <span className="pain-line"></span>
                </div>
                <h3>Ограничения конструкторов</h3>
                <p>Шаблонные сайты на Tilda/WordPress ломаются при любой попытке масштабирования: невозможно настроить сложную базу данных или кастомный личный кабинет.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Screen 3: Capabilities */}
        <section id="capabilities" className="section">
          <span className="section-pretitle text-reveal">Направления работы</span>
          <h2 className="title-main text-reveal">
            Решаем задачи, которые не под силу стандартным решениям
          </h2>
          <div className="features-grid">
            <FeatureCard 
              title="Кастомные веб-системы" 
              text="Личные кабинеты, корпоративные порталы и ERP-системы. Проектируем базы данных и пишем быстрый, чистый код, готовый к высоким нагрузкам." 
            />
            <FeatureCard 
              title="Telegram WebApps" 
              text="Интегрируем полноценные мобильные сервисы прямо внутрь мессенджера Telegram. Продажи, личные кабинеты и поддержка клиентов в один клик." 
            />
            <FeatureCard 
              title="Автоматизация & API" 
              text="Синхронизируем ваш сайт со складским учетом 1С, CRM (AmoCRM, Битрикс24) и внешними сервисами. Полностью убираем человеческий фактор." 
            />
            <FeatureCard 
              title="Интерактивный 3D WebGL" 
              text="3D-визуализации и конфигураторы товаров для премиум-сегмента. Позвольте клиентам взаимодействовать с вашим продуктом прямо в браузере." 
            />
          </div>
        </section>

        {/* Screen 4: Process (7 шагов) */}
        <Process />

        {/* Screen 5: О нас */}
        <section className="section alien-section" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <div className="alien-about-text card-reveal" style={{ 
            width: '100%', 
            maxWidth: '800px', 
            marginLeft: 'auto',
            background: 'radial-gradient(ellipse at center, rgba(2, 6, 23, 0.6) 0%, rgba(2, 6, 23, 0) 80%)',
            borderRadius: '40px'
          }}>
            <h2 className="title-main" style={{ 
              fontSize: 'clamp(2rem, 4vw, 3.5rem)', 
              maxWidth: '100%', 
              whiteSpace: 'nowrap',
              textShadow: '0 4px 24px rgba(0,0,0,0.9), 0 0 10px rgba(0,0,0,1)' 
            }}>
              Мы — Mukvani Systems
            </h2>
            <p className="subtitle" style={{ 
              maxWidth: '100%', 
              color: '#c8cdd5', 
              textShadow: '0 2px 10px rgba(0,0,0,0.8), 0 0 20px rgba(0,0,0,1)', 
              fontWeight: 500 
            }}>
              Команда разработчиков из Томска. Создаём быстрые сайты, автоматизируем бизнес-процессы и делаем так, чтобы каждый вложенный рубль приносил измеримый результат.
            </p>
            <p className="subtitle" style={{ 
              maxWidth: '100%', 
              color: '#9ca3af', 
              fontSize: '1rem', 
              textShadow: '0 2px 10px rgba(0,0,0,0.9)' 
            }}>
              Работаем онлайн по всей России — а в Томске готовы встретиться лично за чашкой кофе ☕
            </p>
          </div>
        </section>

        {/* Screen 6: Портфолио */}
        <section id="portfolio" className="section portfolio-section">
          <h2 className="title-main text-reveal" style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', textAlign: 'center', width: '100%', margin: '0 auto' }}>
            Наши проекты
          </h2>
          <div className="portfolio-grid">
            <CaseCard 
              image="/avtomatizaciya-biznesa-i-portaly.webp"
              title="Тамарин Сад"
              tag="Каталог рассады · Томск"
              features={[
                'Удобный каталог с карточками товаров и ценами',
                'Покупатель выбирает мессенджер для заказа',
                'Автоматизация: заказ сразу приходит владельцу',
              ]}
              link="https://www.tamarin-sad.ru"
            />
            <CaseCard 
              image="/razrabotka-saytov-tomsk.webp"
              title="Давай Закажем"
              tag="Мебельное производство · Томск"
              features={[
                'Квиз-калькулятор стоимости → лиды в CRM',
                'Glassmorphism-дизайн премиум-уровня',
                'Портфолио проектов с профессиональным фото',
              ]}
              link="https://давай-закажем.рф"
            />
            <CaseCard 
              image="/sozdanie-it-produktov-pod-klyuch.webp"
              title="Mukvani Systems"
              tag="3D-визитка · Наш сайт"
              features={[
                '3D WebGL + 2500 частиц в реальном времени',
                'PageSpeed: Accessibility, Best Practices, SEO — 100',
                'Кинематографический прелоадер',
              ]}
              link="https://mukvani.ru"
            />
          </div>
        </section>

        {/* Screen 7: Подвал */}
        <section id="contacts" className="section" style={{ alignItems: 'center', textAlign: 'center', minHeight: '100vh', justifyContent: 'center' }}>
          <FinalCTACard />
        </section>
      </main>

      {/* Floating Sticky CTA */}
      <StickyCTA show={showSticky} onClick={() => setIsQuizOpen(true)} />
      <CookieBanner />
      
      <QuizModal isOpen={isQuizOpen} onClose={() => setIsQuizOpen(false)} />
    </>
  );
}

// ==========================================
// Cookie Banner Component
// ==========================================
const CookieBanner = React.memo(() => {
  const [isVisible, setIsVisible] = useState(() => !localStorage.getItem('cookies_accepted'));

  const handleAccept = () => {
    localStorage.setItem('cookies_accepted', 'true');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="cookie-banner fade-in">
      <div className="cookie-banner-content">
        <Icon name="Lightbulb" size={20} className="cookie-icon" />
        <p>Мы используем cookie-файлы для аналитики и улучшения работы сайта. Продолжая работу, вы соглашаетесь с <a href="/privacy.html" target="_blank" rel="noreferrer">Политикой конфиденциальности</a>.</p>
      </div>
      <button onClick={handleAccept} className="cookie-btn">Понятно</button>
    </div>
  );
});

// ==========================================
// Main Components
// ==========================================
interface HeaderProps {
  onOpenQuiz: () => void;
}

const Header = React.memo(({ onOpenQuiz }: HeaderProps) => {
  const handleScrollTo = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const target = document.querySelector(id);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header className="header-nav">
      <div className="header-container">
        <a href="#" className="logo-link" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
          MUKVANI<span className="logo-slash">/</span>SYSTEMS<span className="logo-dot"></span>
        </a>
        <nav className="nav-menu">
          <a href="#capabilities" onClick={(e) => handleScrollTo(e, '#capabilities')} className="nav-link">Услуги</a>
          <a href="#process" onClick={(e) => handleScrollTo(e, '#process')} className="nav-link">Процесс</a>
          <a href="#portfolio" onClick={(e) => handleScrollTo(e, '#portfolio')} className="nav-link">Кейсы</a>
          <a href="#contacts" onClick={(e) => handleScrollTo(e, '#contacts')} className="nav-link">Контакты</a>
        </nav>
        <button className="btn-header-cta" onClick={onOpenQuiz}>
          Запросить оценку
        </button>
      </div>
    </header>
  );
});


// ==========================================
// КОМПОНЕНТЫ: 7 ЭТАПОВ ПРОЦЕССА РАЗРАБОТКИ
// ==========================================
const Process = React.memo(() => {
  const steps = [
    {
      num: '01',
      title: 'Брифинг',
      desc: 'Снимаем задачу. Обсуждаем проект вживую или по видеосвязи. Нам нужно понять, кто ваши конкуренты и как ваш продукт приносит ценность.'
    },
    {
      num: '02',
      title: 'ТЗ и Логика',
      desc: 'Проектируем функциональные блоки. Описываем пути клиентов, интеграцию с CRM и логику оплат. Ничего лишнего — только то, что реально работает.'
    },
    {
      num: '03',
      title: 'Прототип',
      desc: 'Собираем интерактивный скелет в Figma. Вы увидите логику расположения блоков и переходов до того, как появится первая строка кода.'
    },
    {
      num: '04',
      title: 'Дизайн',
      desc: 'Отрисовываем визуальный язык. Если проект массовый — делаем упор на чистоту и шрифты. Если штучный — разрабатываем 3D-сцены и физику частиц.'
    },
    {
      num: '05',
      title: 'Код',
      desc: 'Собираем сайт на React. Оптимизируем загрузку картинок и шрифтов. Скорость — до 0.5 секунды, без фризов и лишней анимации.'
    },
    {
      num: '06',
      title: 'Связка CRM',
      desc: 'Настраиваем передачу лидов. Заявки падают в AmoCRM, Yclients, Bnovo или 1С. Вы не теряете клиентов, а менеджеры видят всю историю.'
    },
    {
      num: '07',
      title: 'Запуск',
      desc: 'Деплоим в Yandex Cloud, подключаем ваш домен, привязываем SSL. Передаем все доступы и обучаем команду работе с панелью управления.'
    }
  ];

  return (
    <section id="process" className="section process-section">
      <div className="process-header card-reveal">
        <span className="section-pretitle">Архитектура взаимодействия</span>
        <h2 className="title-main" style={{ fontSize: 'clamp(2.2rem, 4vw, 3.5rem)', marginBottom: '1.5rem' }}>Как строится разработка</h2>
        <p className="subtitle" style={{ maxWidth: '750px', marginBottom: '4rem' }}>
          Мы не пишем код ради кода. Наша цель — построить понятный инженерный процесс от первой встречи до запуска готовой системы.
        </p>
      </div>
      <div className="process-grid">
        {steps.map((s, idx) => (
          <div key={idx} className="process-card card-reveal">
            <div className="process-num-wrapper">
              <span className="process-number">{s.num}</span>
              <span className="process-line"></span>
            </div>
            <h3>{s.title}</h3>
            <p>{s.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
});


// ==========================================
// КОМПОНЕНТЫ: СТЕКЛЯННАЯ ЛИПКАЯ КНОПКА (Sticky CTA)
// ==========================================
interface StickyCTAProps {
  show: boolean;
  onClick: () => void;
}

const StickyCTA = React.memo(({ show, onClick }: StickyCTAProps) => {
  return (
    <button 
      className={`sticky-cta-btn ${show ? 'visible' : ''}`}
      onClick={onClick}
      aria-label="Открыть расчет стоимости"
    >
      <div className="sticky-cta-glow"></div>
      <span className="sticky-cta-text">Запросить оценку</span>
    </button>
  );
});


// ==========================================
// КОМПОНЕНТЫ: ДИАГНОСТИЧЕСКИЙ КОНФИГУРАТОР (QuizModal)
// ==========================================
interface QuizModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const featuresData: Record<number, string[]> = {
  1: [
    'Интеграция с AmoCRM / Битрикс24',
    'Квиз-калькулятор стоимости',
    'Онлайн-оплата (эквайринг)',
    'Связка с Telegram-ботом',
    'Нужна помощь архитектора'
  ],
  2: [
    'Сложная фильтрация каталога',
    'Интеграция с AmoCRM / RetailCRM',
    'Модуль бронирования (Bnovo / TravelLine)',
    'Личный кабинет пользователей',
    'Синхронизация каталога с 1С',
    'Нужна помощь архитектора'
  ],
  3: [
    'Авторизация через Telegram',
    'База данных пользователей (PostgreSQL)',
    'Прием платежей (Stars / ЮKassa)',
    'Рассылки и уведомления через бот',
    'Нужна помощь архитектора'
  ],
  4: [
    'Настройка AmoCRM / Битрикс24 с нуля',
    'Связка с системами записи (Yclients)',
    'Интеграция с 1С:УНФ / 1С:Предприятие',
    'Кастомный аналитический дашборд',
    'Нужна помощь архитектора'
  ],
  5: [
    '3D-конфигуратор продукта',
    'Интерактивная 3D-сцена (WebGL)',
    'Есть готовые 3D-модели (GLTF / OBJ)',
    'Нужно смоделировать с нуля',
    'Нужна помощь архитектора'
  ]
};

const timelineData: Record<number, string[]> = {
  0: ['Нужно сделать срочно (в течение месяца)', 'Стандартные сроки (1-3 месяца)', 'Сложный проект (от 3 месяцев)'],
  1: ['Срочно (5-10 дней)', 'Стандартно (2-3 недели)'],
  2: ['Стандартно (3-5 недель)', 'Глубокая разработка (1.5-2 месяца)'],
  3: ['Стандартно (3-5 недель)', 'Глубокая разработка (1.5-2 месяца)'],
  4: ['Стандартно (4-6 недель)', 'Глубокая разработка (2-3 месяца)'],
  5: ['Стандартно (1-1.5 месяца)', 'Масштабный проект (2-4 месяца)']
};

const projectTypeLabels: Record<number, string> = {
  0: 'Описано текстом (своими словами)',
  1: 'Запуск продаж (Лендинг)',
  2: 'Оцифровка бизнеса (Корпоративный сайт)',
  3: 'Мобильный сервис (Telegram WebApp)',
  4: 'Автоматизация (CRM / ERP системы)',
  5: 'Имидж и Wow-эффект (3D WebGL)'
};

const roleLabels: Record<string, string> = {
  founder: 'Владелец / Основатель бизнеса',
  ceo: 'Управляющий партнер / CEO',
  manager: 'Руководитель направления / Product',
  agency: 'Представитель агентства (Партнерство)'
};

const QuizModal = ({ isOpen, onClose }: QuizModalProps) => {
  const [step, setStep] = useState(1);
  const [projectType, setProjectType] = useState<number | null>(null);
  const [customDescription, setCustomDescription] = useState('');
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [readiness, setReadiness] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<string>('');
  const [role, setRole] = useState<string>('');
  const [businessScale, setBusinessScale] = useState<string>('');
  const [contactName, setContactName] = useState<string>('');
  const [contactTelegram, setContactTelegram] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [isPrivacyAccepted, setIsPrivacyAccepted] = useState(false);
  
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setProjectType(null);
      setCustomDescription('');
      setSelectedFeatures([]);
      setReadiness(null);
      setTimeline('');
      setRole('');
      setBusinessScale('');
      setContactName('');
      setContactTelegram('');
      setContactPhone('');
      setIsPrivacyAccepted(false);
      setIsSent(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleProjectTypeSelect = (type: number) => {
    setProjectType(type);
    setSelectedFeatures([]); 
    setTimeline(''); 
    setCustomDescription(''); 
    setStep(2);
  };

  const toggleFeature = (feature: string) => {
    if (selectedFeatures.includes(feature)) {
      setSelectedFeatures(selectedFeatures.filter(f => f !== feature));
    } else {
      setSelectedFeatures([...selectedFeatures, feature]);
    }
  };

  const handleStep2Submit = () => {
    setStep(3);
  };

  const handleReadinessSelect = (val: string) => {
    setReadiness(val);
    setStep(4);
  };

  const handleTimelineSelect = (val: string) => {
    setTimeline(val);
    setStep(5);
  };

  const handleRoleSelect = (val: string) => {
    setRole(val);
    setStep(6);
  };

  const handleBusinessScaleSelect = (val: string) => {
    setBusinessScale(val);
    setStep(7);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const lastSubmitTime = localStorage.getItem('last_lead_submit');
    if (lastSubmitTime) {
      const timeDiff = Date.now() - parseInt(lastSubmitTime, 10);
      if (timeDiff < 30 * 60 * 1000) { 
        alert('Вы уже отправили заявку недавно. Мы свяжемся с вами в ближайшее время!');
        return;
      }
    }

    if (!contactTelegram && !contactPhone) {
      alert('Пожалуйста, укажите хотя бы один способ связи (Telegram или Телефон)');
      return;
    }

    if (!isPrivacyAccepted) {
      alert('Пожалуйста, подтвердите согласие с Политикой обработки персональных данных');
      return;
    }

    setIsSending(true);

    const leadData = {
      projectType,
      customDescription,
      features: selectedFeatures,
      readiness,
      timeline,
      role,
      businessScale,
      name: contactName,
      telegram: contactTelegram,
      phone: contactPhone
    };

    const token = import.meta.env.VITE_TG_BOT_TOKEN || import.meta.env.VITE_TELEGRAM_BOT_TOKEN;
    const chatId = import.meta.env.VITE_TG_CHAT_ID || import.meta.env.VITE_TELEGRAM_CHAT_ID;

    const messageText = `
🔔 *НОВЫЙ ЛИД: Mukvani Systems*
👤 *Имя:* ${leadData.name || 'Не указано'}
💼 *Роль:* ${roleLabels[leadData.role || 'founder']}
📱 *Телефон:* ${leadData.phone || 'Не указан'}
✈️ *Telegram:* ${leadData.telegram || 'Не указан'}

📌 *Проект:* ${projectTypeLabels[leadData.projectType || 0]}
${leadData.projectType === 0 ? `📝 *Описание:* ${leadData.customDescription || 'Не заполнено'}` : `🛠 *Функционал:* ${leadData.features.length > 0 ? leadData.features.join(', ') : 'Не выбрано'}`}
📂 *Готовность:* ${leadData.readiness || 'Не указана'}
🏢 *Масштаб:* ${leadData.businessScale || 'Не указан'}
⏳ *Сроки:* ${leadData.timeline || 'Не указаны'}
    `;

    if (!token || !chatId) {
      console.log('🤖 Telegram Bot API Offline (Missing keys in .env). Data:', leadData);
      console.log('Message template:\n', messageText);
      await new Promise(resolve => setTimeout(resolve, 800)); 
      setIsSending(false);
      setIsSent(true);
      return;
    }

    try {
      const response = await fetch(`https://api.telegram.org/bot${token}/sendMessage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: chatId,
          text: messageText,
          parse_mode: 'Markdown'
        })
      });

      if (response.ok) {
        setIsSent(true);
        localStorage.setItem('last_lead_submit', Date.now().toString());
        if (typeof (window as unknown as { ym?: (id: number, method: string, goal: string) => void }).ym !== 'undefined') {
          (window as unknown as { ym: (id: number, method: string, goal: string) => void }).ym(109593020, 'reachGoal', 'lead_submitted');
        }
      } else {
        alert('Ошибка при отправке. Пожалуйста, напишите нам напрямую в Telegram.');
      }
    } catch (err) {
      console.error(err);
      alert('Не удалось отправить форму. Проверьте интернет-соединение.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="quiz-overlay" onClick={onClose} data-lenis-prevent>
      <div className="quiz-bg-glow-1"></div>
      <div className="quiz-bg-glow-2"></div>
      <div className="quiz-modal-card" onClick={e => e.stopPropagation()} data-lenis-prevent>
        <button className="quiz-close-btn" onClick={onClose} aria-label="Закрыть квиз">×</button>
        
        {!isSent ? (
          <>
            <div className="quiz-progress-wrapper">
              <div className="quiz-progress-text">ШАГ {step} ИЗ 7</div>
              <div className="quiz-progress-bar">
                <div className="quiz-progress-fill" style={{ width: `${(step / 7) * 100}%` }}></div>
              </div>
            </div>

            <div className="quiz-step-content">
              {/* ШАГ 1: ТИП ПРОЕКТА */}
              {step === 1 && (
                <div className="quiz-step-inner animate-slide-fade">
                  <h2>Какую бизнес-задачу должен решить проект?</h2>
                  <p className="step-desc">Выберите основное направление для разработки:</p>
                  <div className="quiz-options-list">
                    <button className="quiz-option-btn" onClick={() => handleProjectTypeSelect(0)}>
                      <span className="option-title"><Icon name="PenTool" className="quiz-icon" size={24} /> Опишу своими словами</span>
                      <span className="option-subtitle">Если у вас нестандартная задача или вы не уверены в типе продукта</span>
                    </button>
                    <button className="quiz-option-btn" onClick={() => handleProjectTypeSelect(1)}>
                      <span className="option-title"><Icon name="Rocket" className="quiz-icon" size={24} /> Запуск продаж (Лендинг)</span>
                      <span className="option-subtitle">Быстрый старт, реклама, генерация лидов и сбор заявок</span>
                    </button>
                    <button className="quiz-option-btn" onClick={() => handleProjectTypeSelect(2)}>
                      <span className="option-title"><Icon name="Building2" className="quiz-icon" size={24} /> Оцифровка бизнеса (Корпоративный сайт)</span>
                      <span className="option-subtitle">Представительство в сети, каталог продукции или сложный портал</span>
                    </button>
                    <button className="quiz-option-btn" onClick={() => handleProjectTypeSelect(3)}>
                      <span className="option-title"><Icon name="Smartphone" className="quiz-icon" size={24} /> Мобильный сервис (Telegram WebApp)</span>
                      <span className="option-subtitle">Удобное приложение внутри мессенджера без установки из сторов</span>
                    </button>
                    <button className="quiz-option-btn" onClick={() => handleProjectTypeSelect(4)}>
                      <span className="option-title"><Icon name="Settings" className="quiz-icon" size={24} /> Автоматизация (CRM / ERP системы)</span>
                      <span className="option-subtitle">Ускорение работы команды, учет товаров и синхронизация баз данных</span>
                    </button>
                    <button className="quiz-option-btn" onClick={() => handleProjectTypeSelect(5)}>
                      <span className="option-title"><Icon name="Sparkles" className="quiz-icon" size={24} /> Имидж и Wow-эффект (3D WebGL)</span>
                      <span className="option-subtitle">Трехмерный интерактивный конфигуратор или премиум промо-сайт</span>
                    </button>
                  </div>
                </div>
              )}

              {/* ШАГ 2: ФУНКЦИОНАЛ ИЛИ ТЕКСТ (РАЗВИЛКА) */}
              {step === 2 && projectType !== null && (
                <div className="quiz-step-inner animate-slide-fade">
                  {projectType === 0 ? (
                    <>
                      <h2>В чем заключается ваша идея?</h2>
                      <p className="step-desc">Опишите задачу своими словами. Чтобы вам было проще, опирайтесь на эти вопросы:</p>
                      <ul className="quiz-hints-list">
                        <li>— Какую ключевую проблему бизнеса должен решить продукт?</li>
                        <li>— Как этот процесс устроен сейчас и что нужно улучшить?</li>
                        <li>— Есть ли жесткие технические требования (интеграция с 1С, CRM и т.д.)?</li>
                      </ul>
                      <textarea 
                        className="quiz-textarea"
                        placeholder="Наш проект — это..."
                        value={customDescription}
                        onChange={(e) => setCustomDescription(e.target.value)}
                        rows={5}
                      ></textarea>
                    </>
                  ) : (
                    <>
                      <h2>Какие возможности и интеграции для вас критичны?</h2>
                      <p className="step-desc">Выберите ключевые ожидания от проекта (можно несколько):</p>
                      <div className="quiz-checkbox-grid">
                        {featuresData[projectType]?.map((feat, idx) => (
                          <div 
                            key={idx} 
                            className={`quiz-checkbox-card ${selectedFeatures.includes(feat) ? 'selected' : ''}`}
                            onClick={() => toggleFeature(feat)}
                          >
                            <div className="checkbox-indicator">
                              {selectedFeatures.includes(feat) && <span className="check-mark">✓</span>}
                            </div>
                            <span className="checkbox-label">{feat}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  <div className="quiz-navigation">
                    <button className="quiz-btn-back" onClick={() => setStep(1)}>Назад</button>
                    <button className="quiz-btn-next" onClick={handleStep2Submit} disabled={projectType === 0 && customDescription.trim().length === 0}><span>Продолжить</span></button>
                  </div>
                </div>
              )}

              {/* ШАГ 3: ГОТОВНОСТЬ МАТЕРИАЛОВ */}
              {step === 3 && (
                <div className="quiz-step-inner animate-slide-fade">
                  <h2>На каком этапе сейчас находится проект?</h2>
                  <p className="step-desc">Мы можем подключиться к работе на любой стадии:</p>
                  <div className="quiz-options-list">
                    <button className="quiz-option-btn" onClick={() => handleReadinessSelect('idea')}>
                      <span className="option-title"><Icon name="Lightbulb" className="quiz-icon" size={24} /> Есть только идея / ТЗ отсутствует</span>
                      <span className="option-subtitle">Мы проведем аналитику, напишем контент и составим ТЗ с нуля</span>
                    </button>
                    <button className="quiz-option-btn" onClick={() => handleReadinessSelect('struct')}>
                      <span className="option-title"><Icon name="LayoutTemplate" className="quiz-icon" size={24} /> Есть базовая структура и тексты</span>
                      <span className="option-subtitle">Логика готова, требуются мощный UX/UI дизайн и программирование</span>
                    </button>
                    <button className="quiz-option-btn" onClick={() => handleReadinessSelect('design')}>
                      <span className="option-title"><Icon name="Palette" className="quiz-icon" size={24} /> Есть готовый дизайн-макет</span>
                      <span className="option-subtitle">Требуется pixel-perfect верстка и техническая реализация логики</span>
                    </button>
                  </div>
                  <div className="quiz-navigation">
                    <button className="quiz-btn-back" onClick={() => setStep(2)}>Назад</button>
                  </div>
                </div>
              )}

              {/* ШАГ 4: СРОКИ */}
              {step === 4 && projectType !== null && (
                <div className="quiz-step-inner animate-slide-fade">
                  <h2>Каковы желаемые сроки реализации?</h2>
                  <p className="step-desc">Сроки зависят от масштаба выбранного вами проекта:</p>
                  <div className="quiz-options-list">
                    {timelineData[projectType]?.map((timeOption, idx) => (
                      <button key={idx} className="quiz-option-btn" onClick={() => handleTimelineSelect(timeOption)}>
                        <span className="option-title">{timeOption}</span>
                      </button>
                    ))}
                  </div>
                  <div className="quiz-navigation">
                    <button className="quiz-btn-back" onClick={() => setStep(3)}>Назад</button>
                  </div>
                </div>
              )}

              {/* ШАГ 5: РОЛЬ */}
              {step === 5 && (
                <div className="quiz-step-inner animate-slide-fade">
                  <h2>Какова ваша роль в проекте?</h2>
                  <p className="step-desc">Это поможет нам подобрать правильного специалиста для первого диалога:</p>
                  <div className="quiz-options-list">
                    {Object.entries(roleLabels).map(([key, label]) => (
                      <button key={key} className="quiz-option-btn" onClick={() => handleRoleSelect(key)}>
                        <span className="option-title">{label}</span>
                      </button>
                    ))}
                  </div>
                  <div className="quiz-navigation">
                    <button className="quiz-btn-back" onClick={() => setStep(4)}>Назад</button>
                  </div>
                </div>
              )}

              {/* ШАГ 6: МАСШТАБ БИЗНЕСА */}
              {step === 6 && (
                <div className="quiz-step-content fade-in">
                  <h2>Масштаб вашего бизнеса</h2>
                  <p className="step-desc">Поможет нам подобрать оптимальную архитектуру и команду для проекта:</p>
                  <div className="quiz-options-list">
                    <button className="quiz-option-btn" onClick={() => handleBusinessScaleSelect('Запускаем новый стартап / MVP')}>
                      <span className="option-title"><Icon name="Rocket" className="quiz-icon" size={24} /> Запускаем новый стартап / MVP</span>
                    </button>
                    <button className="quiz-option-btn" onClick={() => handleBusinessScaleSelect('Малый действующий бизнес (до 50 сотрудников)')}>
                      <span className="option-title"><Icon name="Briefcase" className="quiz-icon" size={24} /> Малый действующий бизнес (до 50 сотрудников)</span>
                    </button>
                    <button className="quiz-option-btn" onClick={() => handleBusinessScaleSelect('Средний или Крупный бизнес (Enterprise)')}>
                      <span className="option-title"><Icon name="Building" className="quiz-icon" size={24} /> Средний или Крупный бизнес (Enterprise)</span>
                    </button>
                  </div>
                </div>
              )}

              {/* ШАГ 7: КОНТАКТЫ */}
              {step === 7 && (
                <form onSubmit={handleSubmit} className="quiz-step-inner animate-slide-fade">
                  <h2>Обсудим ваш проект детальнее</h2>
                  <p className="step-desc">Оставьте контакты. Системный аналитик свяжется с вами, чтобы провести глубинный бриф, обсудить архитектуру и составить план работ.</p>
                  
                  <div className="quiz-form-group">
                    <label>Ваше имя / Компания</label>
                    <input 
                      type="text" 
                      placeholder="Например: Иван, CEO TechCorp"
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="quiz-form-group">
                    <label>Telegram никнейм</label>
                    <input 
                      type="text" 
                      placeholder="@username"
                      value={contactTelegram}
                      onChange={(e) => setContactTelegram(e.target.value)}
                    />
                  </div>

                  <div className="quiz-form-group">
                    <label>Номер телефона</label>
                    <input 
                      type="tel" 
                      placeholder="+7 (999) 000-00-00"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(formatPhoneNumber(e.target.value))}
                    />
                  </div>

                  <span className="form-helper-text">Укажите хотя бы один канал связи для быстрой переписки.</span>

                  <div className="quiz-privacy-checkbox" style={{ marginTop: '1.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <input 
                      type="checkbox" 
                      id="quiz-privacy" 
                      checked={isPrivacyAccepted}
                      onChange={(e) => setIsPrivacyAccepted(e.target.checked)}
                      style={{ marginTop: '4px', cursor: 'pointer', accentColor: '#3b82f6', width: '18px', height: '18px' }}
                    />
                    <label htmlFor="quiz-privacy" style={{ fontSize: '0.85rem', color: '#9ca3af', lineHeight: '1.4', cursor: 'pointer' }}>
                      Я согласен на обработку моих персональных данных в соответствии с <a href="/privacy.html" target="_blank" rel="noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}>Политикой конфиденциальности</a>
                    </label>
                  </div>

                  <div className="quiz-navigation" style={{ marginTop: '2.5rem' }}>
                    <button type="button" className="quiz-btn-back" onClick={() => setStep(5)}>Назад</button>
                    <button type="submit" className="quiz-btn-submit" disabled={isSending || !isPrivacyAccepted} style={{ opacity: isPrivacyAccepted ? 1 : 0.5, cursor: isPrivacyAccepted ? 'pointer' : 'not-allowed' }}>
                      <span>{isSending ? 'Формируем заявку...' : 'Получить персональный Roadmap'}</span>
                    </button>
                  </div>
                </form>
              )}
            </div>
          </>
        ) : (
          <div className="quiz-success-view animate-fade-in">
            <div className="success-icon-wrapper">
              <span className="success-icon">✓</span>
            </div>
            <h2>Проект успешно поставлен в очередь!</h2>
            <p>
              Спасибо за уделенное время. Мы изучили выбранные параметры и уже готовим аналитический разбор для вас. 
            </p>
            <p className="success-bonus-text">
              Ваш промокод на скидку 10%: <strong className="promo-code">MUKVANI_ENGINEER_2026</strong>
            </p>
            <div style={{ marginTop: '2.5rem', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a 
                href="https://t.me/Mukvani_systems" 
                target="_blank" 
                rel="noreferrer" 
                className="btn-primary" 
                style={{ margin: 0, padding: '1rem 2.5rem' }}
                onClick={() => {
                  if (typeof (window as unknown as { ym?: (id: number, method: string, goal: string) => void }).ym !== 'undefined') {
                    (window as unknown as { ym: (id: number, method: string, goal: string) => void }).ym(109593020, 'reachGoal', 'telegram_click');
                  }
                }}
              >
                Перейти в Telegram-чат
              </a>
              <button className="quiz-btn-back" style={{ margin: 0, padding: '1rem 2.5rem' }} onClick={onClose}>
                Закрыть бриф
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


const FinalCTACard = () => {
  const cardRef = useRef<HTMLDivElement>(null);

  const handlePointerUpdate = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const { left, top } = cardRef.current.getBoundingClientRect();
    const x = e.clientX - left;
    const y = e.clientY - top;
    
    cardRef.current.style.setProperty('--mouse-x', `${x}px`);
    cardRef.current.style.setProperty('--mouse-y', `${y}px`);
  };

  return (
    <div 
      className="final-cta-card card-reveal"
      ref={cardRef}
      onPointerMove={handlePointerUpdate}
      onPointerDown={handlePointerUpdate}
      style={{ touchAction: 'pan-y' }}
    >
      <div className="final-cta-content" style={{ position: 'relative', zIndex: 2 }}>
        <h2 className="title-main" style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', marginBottom: '1rem', color: '#ffffff', background: 'none', WebkitTextFillColor: 'initial' }}>
          Ваш проект заслуживает лучшей реализации.
        </h2>
        <p className="subtitle" style={{ margin: '0 auto 3rem auto' }}>
          Обсудим видение и определим вектор развития. Прямая связь:
        </p>
        <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
          <a 
            href="https://t.me/Mukvani_systems" 
            target="_blank" 
            rel="noreferrer" 
            className="btn-primary" 
            style={{ marginTop: 0, padding: '1.2rem 3rem' }}
            onClick={() => {
              if (typeof (window as unknown as { ym?: (id: number, method: string, goal: string) => void }).ym !== 'undefined') {
                (window as unknown as { ym: (id: number, method: string, goal: string) => void }).ym(109593020, 'reachGoal', 'telegram_click');
              }
            }}
          >
            Написать напрямую инженеру
          </a>
          <a 
            href="tel:+79234072270" 
            className="btn-primary" 
            style={{ marginTop: 0, padding: '1.2rem 3rem' }}
            onClick={() => {
              if (typeof (window as unknown as { ym?: (id: number, method: string, goal: string) => void }).ym !== 'undefined') {
                (window as unknown as { ym: (id: number, method: string, goal: string) => void }).ym(109593020, 'reachGoal', 'phone_click');
              }
            }}
          >
            <span>Звонок: +7 (923) 407-22-70</span>
          </a>
        </div>
        
        {/* Интегрированный подвал */}
        <div style={{ marginTop: '4rem', paddingTop: '2rem', borderTop: '1px solid rgba(255,255,255,0.1)', textAlign: 'center', fontSize: '0.85rem', color: '#6b7280', lineHeight: 1.6 }}>
          <p style={{ margin: '0 0 0.5rem 0' }}>© {new Date().getFullYear()} Mukvani Systems. Разработка высоконагруженных IT-решений.</p>
          <p style={{ margin: 0 }}>
            Муквани Я. С., ИНН: 702406531502<br />
            <a href="/privacy.html" target="_blank" rel="noreferrer" style={{ color: '#9ca3af', textDecoration: 'underline', textDecorationColor: 'rgba(156,163,175,0.3)', textUnderlineOffset: '4px' }}>
              Политика обработки персональных данных (152-ФЗ)
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

const FeatureCard = React.memo(({ title, text }: { title: string, text: string }) => {
  const cardRef = useRef<HTMLDivElement>(null);

  const handlePointerUpdate = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const { left, top } = cardRef.current.getBoundingClientRect();
    const x = e.clientX - left;
    const y = e.clientY - top;
    
    cardRef.current.style.setProperty('--mouse-x', `${x}px`);
    cardRef.current.style.setProperty('--mouse-y', `${y}px`);
  };

  return (
    <div 
      ref={cardRef}
      onPointerMove={handlePointerUpdate}
      onPointerDown={handlePointerUpdate}
      className="feature-card card-reveal"
      style={{ touchAction: 'pan-y' }}
    >
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
});

const CaseCard = ({ title, tag, features, link, image }: { title: string, tag: string, features: string[], link: string, image: string }) => {
  const cardRef = useRef<HTMLDivElement>(null);

  const handlePointerUpdate = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const { left, top } = cardRef.current.getBoundingClientRect();
    const x = e.clientX - left;
    const y = e.clientY - top;
    
    cardRef.current.style.setProperty('--mouse-x', `${x}px`);
    cardRef.current.style.setProperty('--mouse-y', `${y}px`);
  };

  return (
    <div 
      className="case-card card-reveal"
      ref={cardRef}
      onPointerMove={handlePointerUpdate}
      onPointerDown={handlePointerUpdate}
      style={{ touchAction: 'pan-y' }}
    >
      <div className="case-card-image" style={{ position: 'relative', zIndex: 2 }}>
        <img src={image} alt={title} loading="lazy" />
      </div>
      <div className="case-card-body" style={{ position: 'relative', zIndex: 2 }}>
        <h3>{title}</h3>
        <div className="case-tag">{tag}</div>
        <ul>
          {features.map((f, i) => <li key={i}>{f}</li>)}
        </ul>
        <a href={link} target="_blank" rel="noreferrer" className="case-card-link">
          Смотреть проект <span>→</span>
        </a>
      </div>
    </div>
  );
};

export default App;
